import { useState } from 'react'

import {
  useApplyRetention,
  useAuditLog,
  useIntegrity,
  useMembers,
  useSetMemberRole,
  useSystemHealth,
  useRetention,
  useSetRetention,
  useVerifyIntegrity,
  type IntegrityResult,
  type RetentionRun,
} from '../api/queries'
import { useTenant } from '../api/tenant'
import type { Session } from '../api/types'
import { Failure } from '../components/Failure'

const ROLES = ['read_only', 'analyst', 'responder', 'admin'] as const

/**
 * Administration — access, the platform's own record, and proof it has not been edited.
 *
 * Integrity verification is the first thing on the page and a button rather than a
 * background job, because a tamper-evident log nobody can check on demand is decoration.
 * The result says in words what "intact" does and does not prove — the sentence comes
 * from the server, so the API, the CLI and this screen cannot drift apart on the one
 * claim it would be most damaging to overstate.
 *
 * The checkpoint's `unverified_since` count sits next to it for the same reason: a
 * checkpoint is where the last successful walk finished, not a promise about anything
 * written since.
 */
export function AdminPage({ tenants = [], session }: { tenants?: string[]; session?: Session }) {
  const [tenant] = useTenant(tenants)
  const [username, setUsername] = useState('')
  const [role, setRole] = useState<string>('analyst')

  const integrity = useIntegrity(tenant)
  const verify = useVerifyIntegrity(tenant)
  const audit = useAuditLog({ tenant })
  const members = useMembers(tenant)
  const health = useSystemHealth(tenant)
  const setRoleFor = useSetMemberRole(tenant)
  const retention = useRetention(tenant)
  const setRetention = useSetRetention(tenant)
  const applyRetention = useApplyRetention(tenant)

  const isAdmin = session?.memberships.some(
    (entry) => entry.tenant === tenant && entry.can_administer,
  )

  if (!isAdmin) {
    return (
      <p className="empty">
        Administration is limited to administrators of this tenant. That includes reading
        the audit log, which records what everybody else did.
      </p>
    )
  }
  if (integrity.isError) {
    return <Failure error={integrity.error} onRetry={() => integrity.refetch()} />
  }

  return (
    <section aria-labelledby="admin-heading">
      <h2 id="admin-heading" style={{ margin: 0 }}>
        Administration
      </h2>

      <section aria-labelledby="integrity-heading" style={{ marginTop: 'var(--space-4)' }}>
        <h3 id="integrity-heading">Audit log integrity</h3>
        <div className="panel">
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-3)',
              flexWrap: 'wrap',
              alignItems: 'baseline',
            }}
          >
            <span className="muted">
              {integrity.data
                ? `${integrity.data.entries} entr${integrity.data.entries === 1 ? 'y' : 'ies'}`
                : 'loading'}
            </span>
            {integrity.data?.checkpoint ? (
              <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                last verified to entry {integrity.data.checkpoint.sequence_no} on{' '}
                {new Date(integrity.data.checkpoint.created_at).toLocaleString()} —{' '}
                {/* A checkpoint is not a promise about anything written since it. */}
                <strong>{integrity.data.checkpoint.unverified_since} entries</strong> written
                since have not been checked
              </span>
            ) : (
              <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                never verified
              </span>
            )}
            <button
              style={{ marginLeft: 'auto' }}
              onClick={() => verify.mutate()}
              disabled={verify.isPending}
            >
              {verify.isPending ? 'Verifying…' : 'Verify now'}
            </button>
          </div>

          {verify.data && <Verdict result={verify.data} />}
        </div>
      </section>

      <section aria-labelledby="health-heading" style={{ marginTop: 'var(--space-5)' }}>
        <h3 id="health-heading">System health</h3>
        {health.data && health.data.degraded.length > 0 && (
          <div className="error" role="alert">
            <strong>{health.data.degraded.join(', ')} not reachable.</strong> Screens that
            read from those will be incomplete or empty, and that is this platform rather
            than the estate.
          </div>
        )}
        <ul style={{ listStyle: 'none', margin: 'var(--space-2) 0 0', padding: 0 }}>
          {(health.data?.results ?? []).map((row) => (
            <li key={row.name} style={{ fontSize: 'var(--text-xs)' }}>
              <span style={{ color: row.ok ? 'var(--ok)' : 'var(--bad)' }}>
                {row.ok ? 'ok' : 'down'}
              </span>{' '}
              <span className="mono">{row.name}</span>{' '}
              <span className="muted">
                {row.endpoint} · {row.latency_ms}ms · {row.detail}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="members-heading" style={{ marginTop: 'var(--space-5)' }}>
        <h3 id="members-heading">Access</h3>
        <form
          className="panel"
          style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}
          onSubmit={(event) => {
            event.preventDefault()
            if (username.trim()) setRoleFor.mutate({ username: username.trim(), role })
          }}
        >
          <label htmlFor="member-username">
            Account
            <input
              id="member-username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="ana"
            />
          </label>
          <label htmlFor="member-role">
            Role
            <select id="member-role" value={role} onChange={(event) => setRole(event.target.value)}>
              {ROLES.map((entry) => (
                <option key={entry} value={entry}>
                  {entry.replace('_', ' ')}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={setRoleFor.isPending || !username.trim()}>
            Grant or change
          </button>
        </form>
        {setRoleFor.isError && (
          <p className="error" role="alert" style={{ marginTop: 'var(--space-2)' }}>
            {String((setRoleFor.error as Error).message)}
          </p>
        )}

        <table style={{ marginTop: 'var(--space-3)' }}>
          <caption className="visually-hidden">Who has access to this tenant</caption>
          <thead>
            <tr>
              <th scope="col">Account</th>
              <th scope="col">Role</th>
              <th scope="col">Can triage</th>
              <th scope="col">Can suppress</th>
              <th scope="col">Can administer</th>
            </tr>
          </thead>
          <tbody>
            {(members.data?.results ?? []).map((row) => (
              <tr key={row.id}>
                <td className="mono">{row.username}</td>
                <td>{row.role.replace('_', ' ')}</td>
                <td>{row.can_triage ? 'yes' : 'no'}</td>
                <td>{row.can_suppress ? 'yes' : 'no'}</td>
                <td>{row.can_administer ? 'yes' : 'no'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section aria-labelledby="retention-heading" style={{ marginTop: 'var(--space-5)' }}>
        <h3 id="retention-heading">Retention</h3>
        {retention.data && (
          <>
            <p className="muted" style={{ fontSize: 'var(--text-xs)' }}>
              {retention.data.configured
                ? 'Records older than these windows are deleted when retention runs.'
                : 'Nothing is set to expire. Zero means keep, which is the default: a platform that starts deleting because nobody configured it loses the evidence for the incident nobody knew was running.'}
            </p>

            <div className="panel">
              {Object.entries(retention.data.policy).map(([name, days]) => (
                <div
                  key={name}
                  style={{
                    display: 'flex',
                    gap: 'var(--space-3)',
                    alignItems: 'baseline',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  <label htmlFor={`retention-${name}`} style={{ minWidth: '20rem' }}>
                    {/* The meaning comes from the server, so the CLI, the API and this
                        screen describe the same window. */}
                    {retention.data?.meanings[name]}
                  </label>
                  <input
                    id={`retention-${name}`}
                    type="number"
                    min={0}
                    defaultValue={days}
                    style={{ width: '6rem' }}
                    onBlur={(event) => {
                      const value = Number(event.target.value)
                      if (Number.isFinite(value) && value >= 0 && value !== days) {
                        setRetention.mutate({ [name]: value })
                      }
                    }}
                  />
                  <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                    days {days === 0 ? '(keep)' : ''}
                  </span>
                </div>
              ))}

              <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
                <button
                  onClick={() => applyRetention.mutate({ confirm: false })}
                  disabled={applyRetention.isPending}
                >
                  Show what would be deleted
                </button>
                {/* The irreversible form needs a second click and says so. */}
                <button
                  onClick={() => applyRetention.mutate({ confirm: true })}
                  disabled={applyRetention.isPending || !retention.data.configured}
                >
                  Delete it now
                </button>
              </div>

              {applyRetention.data && <RetentionOutcome run={applyRetention.data} />}
            </div>

            <div className="error" role="note" style={{ marginTop: 'var(--space-3)' }}>
              <strong>What retention does not touch</strong>
              <ul style={{ marginBottom: 0, fontSize: 'var(--text-xs)' }}>
                {retention.data.not_covered.map((entry) => (
                  <li key={entry.what}>
                    <strong>{entry.what}</strong> — {entry.why}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </section>

      <section aria-labelledby="audit-heading" style={{ marginTop: 'var(--space-5)' }}>
        <h3 id="audit-heading">Audit log</h3>
        <p className="muted" style={{ fontSize: 'var(--text-xs)' }}>
          Newest first. Append-only and hash-chained: a correction is a new entry, never an
          edit. Each row carries the hashes so one entry can be verified independently.
        </p>
        {audit.isError ? (
          <Failure error={audit.error} onRetry={() => audit.refetch()} />
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {(audit.data?.results ?? []).map((entry) => (
              <li
                key={entry.sequence_no}
                className="panel"
                style={{ marginBottom: 'var(--space-2)', padding: 'var(--space-3)' }}
              >
                <div
                  style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'baseline' }}
                >
                  <span className="mono">#{entry.sequence_no}</span>
                  <span className="mono">{entry.action}</span>
                  <span className="muted">{entry.actor}</span>
                  <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                    {new Date(entry.created_at).toLocaleString()}
                  </span>
                </div>
                {Object.keys(entry.payload).length > 0 && (
                  <p className="mono" style={{ margin: 0, fontSize: 'var(--text-xs)' }}>
                    {JSON.stringify(entry.payload)}
                  </p>
                )}
                <p className="muted" style={{ margin: 0, fontSize: 'var(--text-xs)' }}>
                  {entry.row_hash.slice(0, 16)}…
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  )
}

function Verdict({ result }: { result: IntegrityResult }) {
  if (!result.intact) {
    return (
      <div className="error" role="alert" style={{ marginTop: 'var(--space-3)' }}>
        <strong>The audit log has been altered at entry {result.divergence?.sequence_no}.</strong>
        <p style={{ margin: 'var(--space-1) 0 0' }}>{result.divergence?.summary}</p>
        <p style={{ margin: 0, fontSize: 'var(--text-xs)' }}>{result.means}</p>
      </div>
    )
  }
  return (
    <div
      className="panel"
      role="status"
      style={{ borderColor: 'var(--ok)', marginTop: 'var(--space-3)' }}
    >
      <strong>
        {result.entries_checked === 0
          ? 'There was nothing new to check.'
          : `${result.entries_checked} entr${result.entries_checked === 1 ? 'y' : 'ies'} verified.`}
      </strong>
      {/* The server's own sentence, so the API, the CLI and this screen cannot drift
          apart on the one claim it would be most damaging to overstate. */}
      <p style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--text-xs)' }}>{result.means}</p>
    </div>
  )
}

function RetentionOutcome({ run }: { run: RetentionRun }) {
  const classes = Object.entries(run.deleted)
  return (
    <div role="status" style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)' }}>
      <strong>
        {run.dry_run ? 'Would delete' : 'Deleted'} {run.total} record(s).
      </strong>
      <ul style={{ margin: 'var(--space-1) 0 0' }}>
        {classes.map(([name, count]) => (
          <li key={name}>
            {count} — {name.replace('_days', '')}
          </li>
        ))}
        {Object.entries(run.exempt).map(([name, count]) => (
          // Named rather than counted away: the exemptions are the part of a retention
          // policy people are most often surprised by.
          <li key={`exempt-${name}`} style={{ color: 'var(--warn)' }}>
            {count} {name.replace('_days', '')} kept despite their age — they are still in use
          </li>
        ))}
      </ul>
    </div>
  )
}
